<template>
  <div class="cth-settings">
    <a-popover overlay-class-name="cth-popover-overlay" placement="bottomRight" trigger="click" :visible="visible"
      :get-popup-container="resolvePopupContainer" destroy-tooltip-on-hide @visibleChange="onVisibleChange">
      <!-- 触发按钮 -->
      <a-tooltip title="自定义表头" placement="topLeft" :arrow-point-at-center="true">
        <span :class="[
          'cth-trigger-btn',
          border ? 'cth-btn-border' : '',
          mini ? 'cth-btn-mini' : '',
          visible ? 'cth-btn-focus' : '',
        ]">
          <span class="icon-custom"></span>
        </span>
      </a-tooltip>

      <!-- 弹出面板 -->
      <template slot="content">
        <div class="cth-pane-wrapper">
          <!-- 主内容区 -->
          <div class="cth-header-content">
            <!-- 左侧：可选自定义表头 -->
            <div class="cth-table-header">
              <div class="cth-title">可选自定义表头</div>
              <div class="cth-search">
                <a-input-search allow-clear placeholder="请输入检索内容" @change="onHeaderSearchChange" />
              </div>
              <div ref="headerCol" class="cth-col" :style="colWidth ? { width: (colWidth * 2 + 12) + 'px' } : {}">
                <!-- loading -->
                <div v-if="loading" class="cth-spin-wrapper">
                  <a-spin />
                </div>
                <!-- 搜索结果全为空 -->
                <span v-else-if="searchEmptyTreeIndex.length > 0 && searchEmptyTreeIndex.length === treeDatas.length"
                  class="cth-search-empty-text">未找到相关内容</span>
                <!-- 树列表 -->
                <template v-else>
                  <template v-for="(treeData, index) in displayTreeDatas">
                    <template v-if="!searchEmptyTreeIndex.includes(index)">
                      <div :key="'tree-' + index" class="cth-tree-pane">
                        <a-tree checkable :default-expand-all="true" :checked-keys="getTreeCheckedKeys(index)"
                          :check-strictly="checkStrictly" :tree-data="treeData"
                          @check="(keys, info) => handleTreeCheck(keys, info, index)"
                          @select="(keys, info) => handleTreeSelect(keys, info, index)">
                          <template slot="title" slot-scope="node">
                            <a-tooltip :title="getNodeName(node)">
                              <span :class="['cth-node-title', node.className || '']">
                                <span v-if="headerSearch && getNodeName(node)"
                                  v-html="highlightText(getNodeName(node), headerSearch)" />
                                <span v-else>{{ getNodeName(node) }}</span>
                              </span>
                            </a-tooltip>
                            <a-tooltip v-if="node.isMyFollowing" title="我的关注">
                              <a-icon type="star" theme="filled" class="cth-my-following-tag" />
                            </a-tooltip>
                          </template>
                        </a-tree>
                      </div>
                      <a-divider :key="'divider-' + index" class="cth-divider" />
                    </template>
                  </template>
                </template>
              </div>
            </div>

            <!-- 右侧：已选自定义表头 -->
            <div class="cth-selected-header" :style="colWidth ? { width: (colWidth + 16) + 'px' } : {}">
              <div class="cth-title">已选自定义表头</div>
              <div class="cth-search">
                <a-input-search allow-clear placeholder="请输入检索内容" @change="onSelectedSearchChange" />
              </div>
              <div class="cth-col">
                <!-- loading -->
                <div v-if="loading" class="cth-spin-wrapper">
                  <a-spin />
                </div>
                <!-- 空状态 -->
                <div v-else-if="selectedKeys.length === 0" class="cth-empty-wrapper">
                  <a-empty />
                </div>
                <!-- 拖拽排序列表 -->
                <draggable v-else v-model="draggableList" handle=".cth-drag-handle" :move="onDraggableMove"
                  animation="200" class="cth-sortable-list" ghost-class="cth-drag-ghost">
                  <div v-for="(item, index) in draggableList" v-show="isItemVisible(item)" :key="item.key"
                    class="cth-selected-row">
                    <!-- 拖拽手柄 -->
                    <a-tooltip :title="item.sort === false ? '此内容不可调整列顺序' : null">
                      <span :class="[
                        'cth-drag-icon',
                        'cth-drag-handle',
                        item.sort === false ? 'cth-drag-disabled' : '',
                      ]">
                        <a-icon type="menu" />
                      </span>
                    </a-tooltip>

                    <!-- 标题区 -->
                    <span :class="['cth-drag-title', !item.disabled ? 'cth-have-delete-icon' : '']">
                      <a-tooltip :title="item.name">
                        <span :class="['cth-title-name', item.className || '']">
                          <span v-if="selectedSearch && item.name" v-html="highlightText(item.name, selectedSearch)" />
                          <span v-else>{{ item.name }}</span>
                        </span>
                      </a-tooltip>
                      <!-- 固定列图标 -->
                      <a-tooltip v-if="index < fixedColumns" title="最上方表头名称为固定列，表格横向滚动时会浮动在表格左侧">
                        <span class="cth-fixed-column-icon">
                          <a-icon type="pushpin" theme="filled" />
                        </span>
                      </a-tooltip>
                      <!-- 我的关注图标 -->
                      <a-tooltip v-if="item.isMyFollowing" title="我的关注">
                        <span class="cth-my-following-icon">
                          <a-icon type="star" theme="filled" />
                        </span>
                      </a-tooltip>
                    </span>

                    <!-- 删除按钮 -->
                    <span v-if="!item.disabled" class="cth-delete-icon" @click="deleteSelected(item.key)">
                      <a-icon type="close" />
                    </span>
                  </div>
                </draggable>
              </div>
            </div>
          </div>

          <!-- 底部操作栏 -->
          <div class="cth-btn">
            <a-tooltip :title="confirmTooltipTitle" :arrow-point-at-center="true">
              <a-button type="primary" :disabled="confirmDisabled" @click="handleSave">确定</a-button>
            </a-tooltip>
            <a-button @click="cancel">取消</a-button>
            <a-button v-if="!hideReset" @click="reset">恢复默认</a-button>
          </div>
        </div>
      </template>
    </a-popover>
  </div>
</template>

<script>
import { Popover, Tooltip, Input, Tree, Button, Spin, Empty, Divider, Icon } from 'ant-design-vue'
import draggable from 'vuedraggable'
import { cloneDeep, debounce, head, isEmpty, last, isNil, uniq } from 'lodash'
import './customTableHeader.less'


export default {
  name: 'CustomTableHeader',
  components: {
    draggable,
    APopover: Popover,
    ATooltip: Tooltip,
    AInputSearch: Input.Search,
    ATree: Tree,
    AButton: Button,
    ASpin: Spin,
    AEmpty: Empty,
    ADivider: Divider,
    AIcon: Icon,
  },
  props: {
    /** 所有数据，格式为二维数组，每项对应一个树形结构 */
    data: { type: Array, required: true },
    /** 自定义表头的 key，用于 sessionStorage 缓存 */
    customKey: { type: String, default: '' },
    /** 前面固定的列数 */
    fixedColumns: { type: Number, default: undefined },
    /** 点击恢复按钮时使用的数据 */
    recoveryValue: { type: Array, default: () => [] },
    /** 默认选中的值 */
    defaultValue: { type: Array, default: () => [] },
    /** 是否有按钮边框 */
    border: { type: Boolean, default: true },
    /** 是否隐藏恢复默认按钮 */
    hideReset: { type: Boolean, default: false },
    /** 按钮是否为小尺寸 */
    mini: { type: Boolean, default: false },
    /** 节点选择完全受控 */
    checkStrictly: { type: Boolean, default: false },
    /** 数据加载 loading */
    loading: { type: Boolean, default: false },
    /** 弹出框渲染父节点 */
    getPopupContainer: { type: Function, default: null },
    /** 每个分组至少选择一项才允许确认（用于维度/指标场景） */
    requireEachGroup: { type: Boolean, default: false },
  },
  data() {
    return {
      visible: false,
      // 各棵树的选中 key 数组（二维，与 treeDatas 对应）
      selects: [],
      // 整体已选 key 列表（扁平，决定右侧列表顺序）
      selectedKeys: [],
      // 打开浮层时的快照，取消时恢复
      initKeys: [],
      // 列宽度（防止搜索时宽度抖动）
      colWidth: 0,
      // 右侧已选列表搜索词
      selectedSearch: '',
      // 左侧树搜索词
      headerSearch: '',
      // 搜索后的树数据（左侧，仅在 headerSearch 时使用）
      searchDatas: [],
      // 各树是否全为叶子节点（影响布局）
      allLeaf: [],
      // 左侧搜索结果为空的树下标
      searchEmptyTreeIndex: [],
    }
  },
  computed: {
    // requireEachGroup 模式下，每个分组必须至少有一项被选中才允许确认 - By AICode
    confirmDisabled() {
      if (this.requireEachGroup) {
        return this.data.some((_, i) => !this.selects[i] || this.selects[i].length === 0)
      }
      return this.selectedKeys.length === 0
    },
    confirmTooltipTitle() {
      if (!this.confirmDisabled) return null
      if (this.requireEachGroup) return '请至少选择一个维度和一个指标'
      return '请至少选择一项内容'
    },

    // 格式化后的树数据（纯计算，无副作用）
    treeDatas() {
      const formatData = (dataList) => {
        let isFlatData = true
        dataList.forEach((item) => {
          if (typeof item.title === 'string') {
            item.name = item.title
          }
          if (item.children) {
            item.row = isNil(item.row) ? true : item.row
            formatData(item.children)
            isFlatData = false
          }
        })
        return isFlatData
      }
      const tempDatas = cloneDeep(this.data)
      tempDatas.forEach((item, index) => {
        const isFlatData = formatData(item)
        if (isFlatData) {
          const childrenData = cloneDeep(item)
          tempDatas.splice(index, 1, [
            { key: '', title: '', row: true, children: childrenData },
          ])
        }
      })
      return tempDatas
    },

    // 当前展示的树数据（搜索态用 searchDatas，否则用 treeDatas）
    displayTreeDatas() {
      return this.headerSearch ? this.searchDatas : this.treeDatas
    },

    // 右侧已选列表完整数据（按 selectedKeys 顺序排列）
    selectedData() {
      const allData = []
      this.treeDatas.forEach((item) => {
        this.flattenTreeData(item, allData)
      })
      const result = []
      this.selectedKeys.forEach((key) => {
        const idx = allData.findIndex((d) => d.key === key)
        if (idx > -1) {
          result.push(allData[idx])
        }
      })
      return result
    },

    // draggable 双向绑定（get 返回完整已选数据，set 更新 selectedKeys 顺序）
    draggableList: {
      get() {
        return this.selectedData
      },
      set(newList) {
        this.selectedKeys = newList.map((item) => item.key)
      },
    },
  },
  watch: {
    // data 或 defaultValue 变化时重新初始化
    data: {
      handler() {
        this.syncAllLeaf()
        this.initFromProps()
      },
      immediate: true,
    },
    defaultValue: {
      handler() {
        this.initFromProps()
      },
    },
    // treeDatas 变化时同步 allLeaf
    treeDatas(val) {
      this.syncAllLeafFromTreeDatas(val)
    },
    visible(val) {
      if (val) {
        this.$nextTick(() => {
          this.updateColWidth()
        })
      } else {
        this.colWidth = 0
      }
    },
  },
  created() {
    // 防抖搜索函数
    this.onHeaderSearchChange = debounce((e) => {
      this._doHeaderSearch(e && e.target ? e.target.value : e)
    }, 500)
    this.onSelectedSearchChange = debounce((e) => {
      this._doSelectedSearch(e && e.target ? e.target.value : e)
    }, 500)
  },
  mounted() {
    this.syncAllLeaf()
    // 有缓存时触发 save 事件
    if (this.customKey) {
      try {
        const cached = sessionStorage.getItem(this.customKey)
        if (cached) {
          const parsed = JSON.parse(cached)
          if (Array.isArray(parsed) && parsed.length > 0) {
            this.$emit('save', parsed)
          }
        }
      } catch (e) {
        // ignore
      }
    }
  },
  methods: {
    // ─── 初始化 ────────────────────────────────────────────────

    initFromProps() {
      let value = this.defaultValue ? [...this.defaultValue] : []
      if (this.customKey) {
        try {
          const cached = sessionStorage.getItem(this.customKey)
          if (cached) {
            const parsed = JSON.parse(cached)
            if (Array.isArray(parsed) && parsed.length > 0) {
              value = parsed
            }
          }
        } catch (e) {
          // ignore
        }
      }
      this.selects = this.getKeysByValue(this.data, value)
      this.selectedKeys = [...value]
      this.initKeys = [...value]
    },

    syncAllLeaf() {
      this.syncAllLeafFromTreeDatas(this.treeDatas)
    },

    syncAllLeafFromTreeDatas(treeDatas) {
      const dataLeaf = []
      this.data.forEach((item) => {
        let isFlatData = true
        item.forEach((node) => {
          if (node.children) isFlatData = false
        })
        dataLeaf.push(isFlatData)
      })
      this.allLeaf = dataLeaf
      // 使 treeDatas 长度一致
      if (treeDatas && treeDatas.length !== dataLeaf.length) {
        this.allLeaf = treeDatas.map(() => false)
      }
    },

    updateColWidth() {
      setTimeout(() => {
        if (this.visible && this.$refs.headerCol) {
          const w = this.$refs.headerCol.getBoundingClientRect().width
          if (w) {
            this.colWidth = (w - 12) / 2
          }
        } else {
          this.colWidth = 0
        }
      }, 0)
    },

    // ─── 工具方法 ──────────────────────────────────────────────

    // 从原始 data 中提取与 value 匹配的 key，返回二维数组
    getKeysByValue(datas, value) {
      if (!value || !value.length) return []
      const select = []
      datas.forEach((item, index) => {
        select[index] = []
        item.forEach((obj) => {
          if (obj.key && value.indexOf(obj.key) > -1) {
            select[index].push(obj.key)
          }
          if (obj.children) {
            this.collectChildKeys(obj.children, value, index, select)
          }
        })
      })
      return select
    },

    collectChildKeys(datas, value, index, select) {
      datas.forEach((item) => {
        if (item.key && value.indexOf(item.key) > -1) {
          select[index].push(item.key)
        }
        if (item.children) {
          this.collectChildKeys(item.children, value, index, select)
        }
      })
    },

    // 递归拉平树数据，取有 key 的所有节点
    flattenTreeData(treeList, result) {
      treeList.forEach((item) => {
        if (item.key) {
          result.push(item)
        }
        if (item.children) {
          this.flattenTreeData(item.children, result)
        }
      })
    },

    getNodeName(node) {
      return node.name || (typeof node.title === 'string' ? node.title : '')
    },

    // 搜索高亮（返回 HTML 字符串）
    highlightText(text, keyword) {
      if (!keyword || !text) return `<span>${text}</span>`
      const lowerText = text.toLocaleLowerCase()
      const lowerKeyword = keyword.toLocaleLowerCase()
      const idx = lowerText.indexOf(lowerKeyword)
      if (idx === -1) return `<span>${text}</span>`
      const before = text.substring(0, idx)
      const center = text.substring(idx, idx + keyword.length)
      const after = text.slice(idx + keyword.length)
      return `${before}<span class="cth-search-highlight">${center}</span>${after}`
    },

    // 右侧列表是否显示某项（根据 selectedSearch 过滤）
    isItemVisible(item) {
      if (!this.selectedSearch) return true
      return item.name && item.name.toLocaleLowerCase().includes(this.selectedSearch.toLocaleLowerCase())
    },

    // ─── 树勾选逻辑 ────────────────────────────────────────────

    getTreeCheckedKeys(index) {
      const value = this.selects[index] || []
      if (this.checkStrictly) {
        const treeData = this.treeDatas[index] || []
        return this._buildCheckStrictlyKeys(treeData, value)
      }
      return value
    },

    _buildCheckStrictlyKeys(treeDatas, value) {
      const checked = [...value]
      const halfChecked = []
      treeDatas.forEach((item) => {
        if (item.children && !isEmpty(item.children)) {
          const leafKeys = this._getLeafKeys(item.children)
          const selectedCount = leafKeys.filter((k) => value.includes(k)).length
          if (selectedCount === leafKeys.length && selectedCount > 0) {
            if (!checked.includes(item.key)) checked.push(item.key)
          } else if (selectedCount > 0) {
            halfChecked.push(item.key)
          }
        }
      })
      return { checked: uniq(checked), halfChecked }
    },

    _getLeafKeys(datas) {
      const keys = []
      datas.forEach((item) => {
        if (!item.children) {
          keys.push(item.key)
        } else {
          keys.push(...this._getLeafKeys(item.children))
        }
      })
      return keys
    },

    // 收集节点下所有可选叶子节点 key
    _getNodeLeafKeys(node) {
      const leafKeys = []
      const parentKeys = []
      if (node.children) {
        parentKeys.push(node.key)
        this._collectChildLeafs(node.children, leafKeys, parentKeys)
      } else {
        leafKeys.push(node.key)
      }
      return { leafKeys, parentKeys }
    },

    _collectChildLeafs(children, leafKeys, parentKeys) {
      children.forEach((child) => {
        if (child.children) {
          parentKeys.push(child.key)
          this._collectChildLeafs(child.children, leafKeys, parentKeys)
        } else {
          if (!child.disabled) leafKeys.push(child.key)
        }
      })
    },

    // 计算本次操作变化的 key 列表
    _getChangeKeys(node, checked, currentSelects) {
      let changeKeys = []
      if (node.children && !isEmpty(node.children)) {
        node.children.forEach((child) => {
          const included = currentSelects.includes(child.key)
          if (checked && !included && !child.disabled) {
            changeKeys.push(String(child.key))
          } else if (!checked && included && !child.disabled) {
            changeKeys.push(String(child.key))
          }
        })
      } else {
        changeKeys = [String(node.key)]
      }
      return changeKeys
    },

    handleTreeCheck(checkedKeys, info, index) {
      const node = info.node
      const nodeData = node.dataRef || node
      const checked = !node.checked // ant-design-vue 1.x onCheck 中 node.checked 是点击前的状态
      const currentSelects = this.selects[index] || []
      const { leafKeys, parentKeys } = this._getNodeLeafKeys(nodeData)
      const changeKeys = this._getChangeKeys(nodeData, checked, currentSelects)

      let tempKeys = cloneDeep(this.selectedKeys)
      if (checked) {
        const lastItem = last(this.selectedData)
        if (lastItem && lastItem.sort === false && this.selectedData.length > 1) {
          const lastKey = tempKeys.pop()
          if (lastKey) {
            tempKeys = uniq([...tempKeys, ...changeKeys, lastKey])
          }
        } else {
          tempKeys = uniq([...tempKeys, ...changeKeys])
        }
      } else {
        const removeSet = new Set([...leafKeys, ...parentKeys])
        tempKeys = tempKeys.filter((k) => !removeSet.has(k))
      }

      // 同步各树 selects
      const newSelects = cloneDeep(this.selects)
      if (!newSelects[index]) newSelects[index] = []
      if (checked) {
        newSelects[index] = uniq([...newSelects[index], ...leafKeys])
      } else {
        const removeSet = new Set([...leafKeys, ...parentKeys])
        newSelects[index] = newSelects[index].filter((k) => !removeSet.has(k))
      }
      this.selects = newSelects
      this.selectedKeys = tempKeys

      this.$emit('change', tempKeys, changeKeys, nodeData)
    },

    handleTreeSelect(selectedKeys, info, index) {
      // onSelect 触发时，node.selected 是点击后的状态，反转为点击前
      const nodeCheckedBefore = !info.node.selected
      this.handleTreeCheck(selectedKeys, {
        node: { ...info.node, checked: nodeCheckedBefore },
      }, index)
    },

    // ─── 已选列表操作 ──────────────────────────────────────────

    deleteSelected(key) {
      const idx = this.selectedKeys.indexOf(key)
      if (idx !== -1) {
        const newKeys = cloneDeep(this.selectedKeys)
        newKeys.splice(idx, 1)
        this.selectedKeys = newKeys
      }
      const newSelects = cloneDeep(this.selects)
      newSelects.forEach((arr) => {
        if (arr) {
          const i = arr.indexOf(key)
          if (i !== -1) arr.splice(i, 1)
        }
      })
      this.selects = newSelects
    },

    // ─── 拖拽排序 ──────────────────────────────────────────────

    onDraggableMove(evt) {
      const draggedEl = evt.draggedContext
      const relatedEl = evt.relatedContext

      // 禁止 sort===false 的项被拖动
      if (draggedEl.element && draggedEl.element.sort === false) {
        return false
      }

      const firstItem = head(this.selectedData)
      const lastItem = last(this.selectedData)
      const relatedIndex = relatedEl.index

      // 禁止其他项拖到禁用首项之前
      if (relatedIndex === 0 && firstItem && firstItem.sort === false) {
        return false
      }
      // 禁止其他项拖到禁用末项之后
      if (relatedIndex === this.selectedKeys.length - 1 && lastItem && lastItem.sort === false) {
        return false
      }
      return true
    },

    // ─── 搜索 ──────────────────────────────────────────────────

    _doHeaderSearch(value) {
      const searchText = (value || '').trim()
      if (!searchText) {
        this.searchEmptyTreeIndex = []
        this.searchDatas = []
        this.headerSearch = ''
        this.syncAllLeaf()
        return
      }
      const emptyTreeIndex = []
      const allTreeLeaf = []

      const loop = (data, result, treeIndex, isAllLeaf) => {
        data.forEach((item) => {
          if (!item.children) {
            const strTitle = item.name || ''
            if (strTitle.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())) {
              result.push(cloneDeep(item))
            }
          }
          if (item.children) {
            isAllLeaf = item.key ? false : true
            const obj = {
              title: item.title,
              name: item.name,
              key: item.key,
              children: [],
              row: item.row,
            }
            loop(item.children, obj.children, treeIndex, isAllLeaf)
            result.push(obj)
          }
        })
        if (isEmpty(result) && !emptyTreeIndex.includes(treeIndex)) {
          emptyTreeIndex.push(treeIndex)
        }
        return isAllLeaf
      }

      const tempDatas = []
      cloneDeep(this.treeDatas).forEach((item, index) => {
        tempDatas[index] = []
        const isAllLeaf = loop(item, tempDatas[index], index, true)
        allTreeLeaf.push(isAllLeaf)
      })

      this.searchEmptyTreeIndex = emptyTreeIndex
      this.searchDatas = tempDatas
      this.headerSearch = searchText
      this.allLeaf = allTreeLeaf
    },

    _doSelectedSearch(value) {
      this.selectedSearch = (value || '').trim()
    },

    clearSearchState() {
      this.searchDatas = []
      this.selectedSearch = ''
      this.headerSearch = ''
      this.searchEmptyTreeIndex = []
      this.syncAllLeaf()
    },

    // ─── 弹出面板控制 ──────────────────────────────────────────

    onVisibleChange(val) {
      if (this.visible && !val) {
        // 点击外部关闭：恢复到打开时快照，不保存
        this.selects = this.getKeysByValue(this.data, this.initKeys)
        this.selectedKeys = [...this.initKeys]
        this.clearSearchState()
      }
      this.visible = val
    },

    // ─── 底部操作按钮 ──────────────────────────────────────────

    handleSave() {
      this.visible = false
      const keys = [...this.selectedKeys]
      this.initKeys = keys
      this.clearSearchState()
      if (this.customKey) {
        try {
          sessionStorage.setItem(this.customKey, JSON.stringify(keys))
        } catch (e) {
          // ignore
        }
      }
      this.$emit('save', keys)
      this.$emit('change', keys)
    },

    cancel() {
      this.selects = this.getKeysByValue(this.data, this.initKeys)
      this.selectedKeys = [...this.initKeys]
      this.visible = false
      this.clearSearchState()
    },

    reset() {
      const resetKeys = this.recoveryValue ? [...this.recoveryValue] : []
      this.selects = this.getKeysByValue(this.data, resetKeys)
      this.selectedKeys = [...resetKeys]
      this.handleSave()
    },

    // ─── Popover 容器 ──────────────────────────────────────────

    resolvePopupContainer(triggerNode) {
      if (this.getPopupContainer) {
        return this.getPopupContainer(triggerNode)
      }
      return (
        document.getElementById('bonree-one-basic-content') ||
        triggerNode.parentNode ||
        document.body
      )
    },
  },
}
</script>
